import { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  keyframes,
  Drawer,
} from "@mui/material";
import { Close, Refresh } from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";

import { APP_API_V1_BASE } from "../../../config/api";
// ── Animations ──────────────────────────────────────────────────────────────
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const pulse = keyframes`0%, 100% { opacity: 1; } 50% { opacity: 0.4; }`;
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Action config ────────────────────────────────────────────────────────────
const ACTION_CONFIG = {
  DELETE: { color: "#B91C1C", dot: "#EF4444", label: "DELETE" },
  REGENERATE: { color: "#C2410C", dot: "#F97316", label: "REGEN" },
  CREATE: { color: "#15803D", dot: "#22C55E", label: "CREATE" },
  UPDATE: { color: "#1D4ED8", dot: "#3B82F6", label: "UPDATE" },
};

const getActionConfig = (value = "") => {
  const key = Object.keys(ACTION_CONFIG).find((k) => value.includes(k));
  return key
    ? ACTION_CONFIG[key]
    : { bg: "#F8FAFC", color: "#475569", border: "#E2E8F0", dot: "#94A3B8" };
};

// ── Row accent color (left border) ──────────────────────────────────────────
const getRowAccent = (action = "") => {
  if (action.includes("DELETE")) return "#EF4444";
  if (action.includes("REGENERATE")) return "#F97316";
  if (action.includes("CREATE")) return "#22C55E";
  if (action.includes("UPDATE")) return "#3B82F6";
  return "#CBD5E1";
};
const getSeverityStyles = (level) => {
  switch (level) {
    case "error":
      return {
        bg: "#FEF2F2",
        color: "#B91C1C",
        border: "#FECACA",
        dot: "#EF4444",
        accent: "#EF4444",
      };
    case "warn":
      return {
        bg: "#FFF7ED",
        color: "#C2410C",
        border: "#FED7AA",
        dot: "#F97316",
        accent: "#F97316",
      };
    case "info":
      return {
        bg: "#EFF6FF",
        color: "#1D4ED8",
        border: "#BFDBFE",
        dot: "#3B82F6",
        accent: "#3B82F6",
      };
    default:
      return {
        bg: "#F8FAFC",
        color: "#475569",
        border: "#E2E8F0",
        dot: "#94A3B8",
        accent: "#CBD5E1",
      };
  }
};
const getAvatarColor = (char) => {
  const colors = [
    { bg: "#E0E7FF", color: "#3730A3" },
    { bg: "#D1FAE5", color: "#065F46" },
    { bg: "#FEF3C7", color: "#92400E" },
    { bg: "#FCE7F3", color: "#9D174D" },
  ];
  return colors[char.charCodeAt(0) % colors.length];
};

export default function AuditLogsTable({ entityType, entityId, height = 400 }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(4);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  useEffect(() => {
  fetchLogs();
}, []);
  const isMobile = useMediaQuery("(max-width:600px)");

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const params = {};
      if (entityType) params.entity_type = entityType;
      if (entityId) params.entity_id = entityId;
      const res = await axios.get(`${APP_API_V1_BASE}/audit-logs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });
      setLogs(res.data || []);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Failed to load audit logs", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };
  const handleRowClick = (params) => {
    setSelectedRow(params.row);
    setDrawerOpen(true);
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
  {
    field: "created_at",
    headerName: "Timestamp",
    minWidth: 170,
    flex: 1,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Typography>
          {new Date(params.value).toLocaleString()}
        </Typography>
      </Box>
    ),
  },
  {
  field: "action",
  headerName: "Action",
  minWidth: 210,
  headerAlign: "center",   // ✅ align header
  align: "center",         // ✅ align cell horizontally
  renderCell: ({ value }) => {
    const cfg = getActionConfig(value);
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",   // ✅ center horizontally
          height: "100%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.7,
            fontSize: 11,
            fontWeight: 700,
            px: 1.2,
            py: 0.5,
            borderRadius: "6px",
            letterSpacing: "0.04em",
            background: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
            maxWidth: 190,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {/* Colored dot */}
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: cfg.dot,
              flexShrink: 0,
            }}
          />
          {value}
        </Box>
      </Box>
    );
  },
},
  {
    field: "performed_by",
    headerName: "Performed By",
    minWidth: 180,
    renderCell: ({ value }) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
          gap: 1,
        }}
      >
        <Typography
          sx={{ fontSize: 12, color: "text.primary", fontWeight: 500 }}
        >
          {value || "—"}
        </Typography>
      </Box>
    ),
  },
  {
    field: "metadata",
    headerName: "Details",
    flex: 1.2,
    minWidth: 200,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Typography sx={{ fontSize: 12 }}>
          {params.value ? JSON.stringify(params.value) : "-"}
        </Typography>
      </Box>
    ),
  },
];
  function DetailRow({ label, value, mono = false, chip = null }) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          py: 1.4,
          borderBottom: "0.5px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            color: "text.secondary",
            fontWeight: 500,
            minWidth: 90,
            flexShrink: 0,
            pt: 0.1,
          }}
        >
          {label}
        </Typography>
        {chip ?? (
          <Typography
            sx={{
              fontSize: 12,
              color: "text.primary",
              fontWeight: 500,
              textAlign: "right",
              wordBreak: "break-all",
              fontFamily: mono
                ? "'JetBrains Mono','Fira Code',monospace"
                : "inherit",
            }}
          >
            {value || "—"}
          </Typography>
        )}
      </Box>
    );
  }
  const sev = selectedRow ? getSeverityStyles(selectedRow.level) : null;
  const avatarChar = selectedRow
    ? (selectedRow.user || "D").charAt(0).toUpperCase()
    : "D";
  const avatarColors = getAvatarColor(avatarChar);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Box
        sx={{
          width: "100%",
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          boxShadow: "0 1px 4px 0 rgba(16,24,40,0.06)",
          animation: `${fadeSlideIn} 0.3s ease`,
        }}
      >
        {/* ── Header ── */}
        <Box
          px={2}
          py={1.5}
          display={isMobile ? "flex-wrap" : "flex"}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid #e5e7eb"
        >
          {/* Left */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Live pulse dot */}
            <Box
              sx={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: "#185FA5",
                  opacity: 0.25,
                  animation: `${pulse} 2s ease-in-out infinite`,
                  transform: "scale(2)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: "#185FA5",
                }}
              />
            </Box>

            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: "-0.01em",
              }}
            >
              Audit Activity
            </Typography>
            <Chip label={`${logs.length} events`} size="small" />
          </Box>

          {/* Right */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "6px",
                  px: 1,
                  py: 0.4,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: "#22C55E",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 11 }}
                >
                  Updated{" "}
                  {lastRefreshed.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>
            )}

            <Tooltip title="Refresh logs">
              <IconButton
                size="small"
                onClick={fetchLogs}
                disabled={loading}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  width: 30,
                  height: 30,
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#F1F5F9" },
                  "& svg": {
                    fontSize: 15,
                    animation: loading
                      ? `${spin} 0.8s linear infinite`
                      : "none",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── DataGrid ── */}
        <Box sx={{ height, width: "100%" }}>
          <DataGrid
            rows={logs}
            columns={columns}
            density="comfortable"
            loading={loading}
            getRowId={(row) => row.id}
            disableColumnMenu
            page={page}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(ps) => {
              setPageSize(ps);
              setPage(0);
            }}
            rowsPerPageOptions={[4, 10, 25, 100]}
            pagination
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              border: "none",
              fontFamily: "inherit",

              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F8FAFC",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "#94A3B8",
                borderBottom: "1px solid",
                borderColor: "divider",
                minHeight: "38px !important",
                maxHeight: "38px !important",
                lineHeight: "38px !important",
              },
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },

              "& .MuiDataGrid-cell": {
                fontSize: 13,
                borderBottom: "1px solid",
                borderColor: "#F1F5F9",
                py: 1,
              },

              "& .MuiDataGrid-row": {
                transition: "background 0.15s ease",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: "#F8FAFC",
                "& .MuiDataGrid-cell": { borderColor: "#E2E8F0" },
              },

              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "#F8FAFC",
                fontSize: 11,
                minHeight: 44,
              },
              "& .MuiTablePagination-root": { fontSize: 11 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: 11,
                  color: "text.secondary",
                },

              "& .MuiDataGrid-virtualScroller": { bgcolor: "#FFFFFF" },
              "& .MuiDataGrid-overlay": { bgcolor: "rgba(255,255,255,0.8)" },
            }}
            slots={{
              loadingOverlay: () => (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                  }}
                >
                  <CircularProgress
                    size={18}
                    thickness={4}
                    sx={{ color: "#185FA5" }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: 12 }}
                  >
                    Fetching logs…
                  </Typography>
                </Box>
              ),
              noRowsOverlay: () => (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    color: "text.secondary",
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      bgcolor: "#F1F5F9",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 0.5,
                      fontSize: 18,
                    }}
                  >
                    📋
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    No activity recorded
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Audit events will appear here as they occur
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>
      </Box>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 380 },
            borderLeft: "1px solid",
            borderColor: "divider",
            boxShadow: "-4px 0 24px rgba(0,0,0,0.07)",
          },
        }}
      >
        {selectedRow && sev && (
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Drawer Header */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "#FAFAFA",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    bgcolor: sev.bg,
                    border: `1px solid ${sev.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      bgcolor: sev.dot,
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: "text.primary",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Audit Details
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "text.secondary",
                      fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    }}
                  >
                    #{selectedRow.id || "—"}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setDrawerOpen(false)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  width: 30,
                  height: 30,
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#F1F5F9" },
                }}
              >
                <Close sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>

            {/* Severity Banner */}
            <Box
              sx={{
                mx: 2.5,
                mt: 2,
                px: 2,
                py: 1.2,
                borderRadius: "8px",
                bgcolor: sev.bg,
                border: `1px solid ${sev.border}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: sev.dot,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: sev.color,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {selectedRow.level || "unknown"} severity
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: 11, color: sev.color, opacity: 0.8 }}>
                {new Date(selectedRow.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </Typography>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, pt: 2.5, pb: 4 }}>
              {/* Section: Event */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 0.8,
                }}
              >
                Event
              </Typography>
              <Box
                sx={{
                  bgcolor: "#FAFAFA",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  px: 2,
                  mb: 2.5,
                }}
              >
                <DetailRow
                  label="Timestamp"
                  value={new Date(selectedRow.created_at).toLocaleString()}
                />
                <DetailRow label="Type" value={selectedRow.type || "SYSTEM"} />
                <DetailRow label="Action" value={selectedRow.action || "—"} />
                <DetailRow label="Target" value={selectedRow.target || "—"} />
              </Box>

              {/* Section: Actor */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 0.8,
                }}
              >
                Actor
              </Typography>
              <Box
                sx={{
                  bgcolor: "#FAFAFA",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  px: 2,
                  mb: 2.5,
                }}
              >
                <DetailRow
                  label="User / Device"
                  chip={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "5px",
                          // bgcolor: avatarColors.bg,
                          // color: avatarColors.color,
                          fontSize: 9,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {/* {avatarChar} */}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                      >
                        {selectedRow.user || "Device"}
                      </Typography>
                    </Box>
                  }
                />
                <DetailRow
                  label="IP Address"
                  value={selectedRow.ip || "—"}
                  mono
                />
              </Box>

              {/* Section: Metadata (if present) */}
              {selectedRow.metadata && (
                <>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "text.secondary",
                      mb: 0.8,
                    }}
                  >
                    Metadata
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "#F8FAFC",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "10px",
                      p: 1.5,
                      mb: 2.5,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: "'JetBrains Mono','Fira Code',monospace",
                        fontSize: 11.5,
                        color: "text.secondary",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(selectedRow.metadata, null, 2)}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Section: Raw */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 0.8,
                }}
              >
                Raw Log
              </Typography>
              <Box
                sx={{
                  bgcolor: "#F8FAFC",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  p: 1.5,
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    fontSize: 11,
                    color: "text.secondary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    margin: 0,
                  }}
                >
                  {JSON.stringify(selectedRow, null, 2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
}
